import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle } from "lucide-react";
import { StatusBadge, FilterTabs, fmtStamp } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ["all", "new", "replied", "closed"];

const waLink = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  return `https://wa.me/${digits.length === 10 ? "91" + digits : digits}`;
};

function EnquiryRow({ e, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const setStatus = async (status) => {
    setBusy(true);
    try {
      const r = await axios.patch(
        `${API}/admin/enquiries/${e.id}`,
        { status },
        { withCredentials: true }
      );
      onUpdate(r.data);
      toast.success(`Enquiry marked ${status}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      data-testid={`admin-enquiry-row-${e.id}`}
      className="rounded-3xl border border-sand bg-white p-6 md:p-7 grid lg:grid-cols-12 gap-5"
    >
      <div className="lg:col-span-3">
        <StatusBadge value={e.status || "new"} testId={`admin-enquiry-status-${e.id}`} />
        <p className="mt-3 font-serif text-xl text-coal">{e.name}</p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{e.interest}</p>
        <p className="mt-2 text-xs text-sage">{fmtStamp(e.created_at)}</p>
      </div>
      <div className="lg:col-span-6">
        <p className="text-sm leading-relaxed text-coal/85 whitespace-pre-line">{e.message}</p>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
          <a href={`tel:${e.phone}`} className="inline-flex items-center gap-2 text-pine hover:text-royal">
            <Phone className="h-3.5 w-3.5" /> {e.phone}
          </a>
          {e.email && (
            <a href={`mailto:${e.email}`} className="inline-flex items-center gap-2 text-pine hover:text-royal break-all">
              <Mail className="h-3.5 w-3.5" /> {e.email}
            </a>
          )}
        </div>
      </div>
      <div className="lg:col-span-3 flex flex-wrap lg:flex-col gap-2 lg:items-end">
        <a
          data-testid={`admin-enquiry-whatsapp-${e.id}`}
          href={waLink(e.phone)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-full border border-[#25D366] bg-[#25D366] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ink hover:bg-[#1ebe5b] transition-colors duration-300"
        >
          <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
        </a>
        {(e.status || "new") !== "replied" && (
          <button
            data-testid={`admin-enquiry-replied-${e.id}`}
            disabled={busy}
            onClick={() => setStatus("replied")}
            className="inline-flex items-center rounded-full border border-pine bg-pine px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-cream hover:bg-royal transition-colors duration-300 disabled:opacity-50"
          >
            Mark replied
          </button>
        )}
        {(e.status || "new") !== "closed" && (
          <button
            data-testid={`admin-enquiry-closed-${e.id}`}
            disabled={busy}
            onClick={() => setStatus("closed")}
            className="inline-flex items-center rounded-full border border-sand bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-sage hover:border-pine hover:text-pine transition-colors duration-300 disabled:opacity-50"
          >
            Close
          </button>
        )}
        {(e.status || "new") === "closed" && (
          <button
            data-testid={`admin-enquiry-reopen-${e.id}`}
            disabled={busy}
            onClick={() => setStatus("new")}
            className="inline-flex items-center rounded-full border border-sand bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-sage hover:border-pine hover:text-pine transition-colors duration-300 disabled:opacity-50"
          >
            Reopen
          </button>
        )}
      </div>
    </article>
  );
}

export default function AdminEnquiries({ enquiries, setEnquiries }) {
  const [filter, setFilter] = useState("all");
  const counts = useMemo(() => {
    const c = { all: enquiries.length };
    FILTERS.slice(1).forEach((f) => (c[f] = enquiries.filter((e) => (e.status || "new") === f).length));
    return c;
  }, [enquiries]);
  const shown = filter === "all" ? enquiries : enquiries.filter((e) => (e.status || "new") === filter);
  const onUpdate = (u) => setEnquiries((list) => list.map((e) => (e.id === u.id ? u : e)));

  return (
    <div data-testid="admin-enquiries-panel">
      <FilterTabs options={FILTERS} value={filter} onChange={setFilter} counts={counts} prefix="admin-enquiries" />
      <div className="mt-6 grid gap-4">
        {shown.length === 0 ? (
          <p data-testid="admin-enquiries-empty" className="rounded-3xl border border-dashed border-sand bg-white/60 p-10 text-center text-sm text-sage">
            No enquiries here yet.
          </p>
        ) : (
          shown.map((e) => <EnquiryRow key={e.id} e={e} onUpdate={onUpdate} />)
        )}
      </div>
    </div>
  );
}
